<script>
import PageHeader from '../../SettingsSubPageHeader.vue';
import telegramPersonalGateway from 'dashboard/api/telegramPersonalGateway';
import PersonalMessenger from './PersonalMessenger.vue';

export default {
  components: {
    PageHeader,
    PersonalMessenger,
  },
  data() {
    return {
      ready: null,
      status: null,
      qr: null,
      historyImport: null,
      error: '',
      isLoading: false,
      isImportLoading: false,
      poller: null,
    };
  },
  computed: {
    isConnected() {
      return this.status?.authorized;
    },
    checks() {
      return this.ready?.checks || {};
    },
    gatewayChecks() {
      const labels = {
        db: 'Хранилище gateway',
        chatwoot_config: 'Связь с Chatwoot',
        telegram_credentials: 'Telegram API',
        telegram_authorized: 'Telegram-аккаунт',
      };
      const pendingLabels = {
        telegram_credentials: 'Не настроен',
        telegram_authorized: 'Не подключён',
      };

      return Object.entries(this.checks).map(([key, value]) => ({
        key,
        label: labels[key] || key,
        isReady: value === 'ok',
        value: value === 'ok' ? 'Готово' : pendingLabels[key] || 'Недоступно',
      }));
    },
    credentialsReady() {
      return this.checks.telegram_credentials === 'ok';
    },
    apiInbox() {
      return this.ready?.api_inbox || null;
    },
    qrSrc() {
      return this.qr?.qr_png_base64
        ? `data:image/png;base64,${this.qr.qr_png_base64}`
        : '';
    },
    historyJob() {
      return this.historyImport?.job || null;
    },
    historyImportRunning() {
      return ['running', 'stopping'].includes(this.historyJob?.status);
    },
  },
  mounted() {
    this.refreshAll();
    this.poller = window.setInterval(this.refreshStatus, 3000);
  },
  unmounted() {
    if (this.poller) {
      window.clearInterval(this.poller);
    }
  },
  methods: {
    async refreshAll() {
      await Promise.all([
        this.refreshReady(),
        this.refreshStatus(),
        this.refreshHistoryImportStatus(),
      ]);
      if (this.credentialsReady && !this.isConnected) {
        await this.refreshQr();
      }
    },
    async refreshReady() {
      try {
        const { data } = await telegramPersonalGateway.ready();
        this.ready = data;
      } catch (error) {
        this.error =
          error.response?.data?.error ||
          'Telegram Personal Gateway не настроен. Проверьте переменные окружения на сервере.';
      }
    },
    async refreshStatus() {
      try {
        const { data } = await telegramPersonalGateway.status();
        this.status = data;
        if (this.credentialsReady && !data.authorized && !this.qr) {
          await this.refreshQr();
        }
      } catch (error) {
        this.error =
          error.response?.data?.error || 'Не удалось проверить статус gateway.';
      }
    },
    async refreshQr() {
      try {
        this.isLoading = true;
        const { data } = await telegramPersonalGateway.qr();
        this.qr = data;
      } catch (error) {
        this.error = error.response?.data?.error || 'Не удалось получить QR-код.';
      } finally {
        this.isLoading = false;
      }
    },
    async disconnect() {
      // eslint-disable-next-line no-alert
      const confirmed = window.confirm(
        'Отключить Telegram account от Telegram Personal Gateway?'
      );
      if (!confirmed) return;

      try {
        this.isLoading = true;
        await telegramPersonalGateway.logout();
        this.status = { authorized: false, status: 'logged_out' };
        this.qr = null;
        await this.refreshAll();
      } catch (error) {
        this.error =
          error.response?.data?.error || 'Не удалось отключить Telegram account.';
      } finally {
        this.isLoading = false;
      }
    },
    async refreshHistoryImportStatus() {
      try {
        const { data } = await telegramPersonalGateway.historyImportStatus();
        this.historyImport = data;
      } catch (error) {
        this.error =
          error.response?.data?.error || 'Не удалось проверить статус импорта истории.';
      }
    },
    async startHistoryImport() {
      const confirmed = window.confirm(
        'Импорт создаст диалоги в sdchat на основе текущей Telegram-сессии. Запустить импорт истории?'
      );
      if (!confirmed) return;

      try {
        this.isImportLoading = true;
        await telegramPersonalGateway.startHistoryImport();
        await this.refreshHistoryImportStatus();
      } catch (error) {
        this.error =
          error.response?.data?.error || 'Не удалось запустить импорт истории.';
      } finally {
        this.isImportLoading = false;
      }
    },
    async stopHistoryImport() {
      try {
        this.isImportLoading = true;
        await telegramPersonalGateway.stopHistoryImport();
        await this.refreshHistoryImportStatus();
      } catch (error) {
        this.error =
          error.response?.data?.error || 'Не удалось остановить импорт истории.';
      } finally {
        this.isImportLoading = false;
      }
    },
  },
};
</script>

<template>
  <div class="h-full w-full p-6 col-span-6 max-w-4xl">
    <PageHeader
      header-title="Telegram Personal"
      header-content="Подключите обычный Telegram-аккаунт через QR-код. Сообщения будут попадать в Chatwoot через Telegram Personal Gateway."
    />

    <div
      v-if="error"
      class="p-4 mb-4 rounded-xl border border-n-ruby-5 bg-n-ruby-3 text-n-ruby-11"
    >
      {{ error }}
    </div>

    <section class="mb-6 border-b border-n-weak pb-6">
      <h2 class="text-lg font-semibold text-n-slate-12">
        Дополнительные Telegram-аккаунты
      </h2>
      <p class="mb-4 mt-1 text-sm text-n-slate-11">
        Добавляйте несколько Telegram Personal устройств. Для каждого
        автоматически создаётся отдельный Inbox, session и хранилище диалогов.
      </p>
      <PersonalMessenger
        provider="telegram_personal"
        title="Telegram Personal"
        description=""
        auth-mode="qr"
        embedded
      />
    </section>

    <h2 class="mb-3 text-lg font-semibold text-n-slate-12">
      Основной Telegram-аккаунт
    </h2>

    <section class="p-4 mb-4 border border-n-weak rounded-xl bg-n-solid-1">
      <h3 class="mb-1 text-base font-semibold text-n-slate-12">
        Состояние подключения
      </h3>
      <p class="mb-3 text-sm text-n-slate-11">
        Технические сервисы и готовность Telegram-аккаунта.
      </p>
      <div class="grid gap-2 sm:grid-cols-2">
        <div
          v-for="check in gatewayChecks"
          :key="check.key"
          class="flex items-center justify-between gap-3 rounded-lg bg-n-alpha-2 px-3 py-2"
        >
          <span class="text-sm text-n-slate-11">{{ check.label }}</span>
          <span
            class="text-sm font-medium"
            :class="check.isReady ? 'text-n-teal-11' : 'text-n-amber-11'"
          >
            {{ check.value }}
          </span>
        </div>
      </div>
    </section>

    <section class="p-4 mb-4 border border-n-weak rounded-xl bg-n-solid-1">
      <div v-if="isConnected">
        <p
          class="inline-flex px-3 py-2 rounded-lg bg-n-teal-3 text-n-teal-11 font-medium"
        >
          Telegram-аккаунт подключён
        </p>
        <p class="mt-3 text-sm text-n-slate-11">
          Client: {{ status.client_identity || 'SDC Client 1.0' }}
        </p>
        <p
          v-if="status.user?.username"
          class="mt-1 text-sm text-n-slate-11"
        >
          Username: @{{ status.user.username }}
        </p>
      </div>

      <div v-else>
        <h3 class="text-base font-semibold text-n-slate-12 mb-3">
          Подключите Telegram-аккаунт
        </h3>
        <div
          v-if="!credentialsReady"
          class="mb-4 rounded-xl border border-n-amber-5 bg-n-amber-3 p-3 text-sm text-n-amber-11"
        >
          Telegram API ещё не настроен администратором сервера. QR-код будет
          доступен после настройки application credentials.
        </div>
        <ol v-else class="list-decimal pl-5 mb-4 text-sm text-n-slate-11">
          <li>Откройте Telegram на телефоне</li>
          <li>Настройки → Устройства</li>
          <li>Подключить устройство</li>
          <li>Отсканируйте QR-код</li>
        </ol>
        <div
          v-if="credentialsReady"
          class="w-80 max-w-full aspect-square grid place-items-center border border-n-weak rounded-xl bg-white"
        >
          <img
            v-if="qrSrc"
            :src="qrSrc"
            alt="Telegram Personal QR login"
            class="w-72 max-w-[92%] h-auto"
          />
          <span v-else class="text-sm text-slate-500">QR-код загружается…</span>
        </div>
        <p v-if="qr?.expires_in" class="mt-2 text-sm text-n-slate-11">
          QR обновится через {{ qr.expires_in }} сек.
        </p>
      </div>

      <div v-if="credentialsReady" class="flex flex-wrap gap-2 mt-4">
        <button
          class="button nice"
          type="button"
          :disabled="isLoading"
          @click="refreshQr"
        >
          Обновить QR
        </button>
        <button
          class="button clear"
          type="button"
          :disabled="isLoading"
          @click="refreshAll"
        >
          Проверить статус
        </button>
        <button
          v-if="isConnected"
          class="button alert"
          type="button"
          :disabled="isLoading"
          @click="disconnect"
        >
          Отключить аккаунт
        </button>
      </div>
    </section>

    <section class="p-4 border border-n-weak rounded-xl bg-n-solid-1">
      <h3 class="text-base font-semibold text-n-slate-12 mb-3">
        Chatwoot API Inbox
      </h3>
      <div v-if="apiInbox?.exists">
        <p class="text-sm text-n-slate-11">Name: {{ apiInbox.name }}</p>
        <p class="text-sm text-n-slate-11">Inbox ID: {{ apiInbox.id }}</p>
        <p class="text-sm text-n-slate-11 break-all">
          Callback URL: {{ apiInbox.callback_url }}
        </p>
        <p class="text-sm text-n-teal-11 mt-2">Статус: настроен</p>
      </div>
      <div v-else class="text-sm text-n-slate-11">
        API Inbox не найден. Создайте API Inbox вручную с именем
        <strong>Telegram Personal</strong> или <strong>Telegram Personal MVP</strong>
        и укажите callback gateway.
      </div>
    </section>

    <section class="p-4 mt-4 border border-n-weak rounded-xl bg-n-solid-1">
      <h3 class="text-base font-semibold text-n-slate-12 mb-3">
        История Telegram
      </h3>
      <p class="text-sm text-n-slate-11 mb-3">
        Импорт создаст диалоги в sdchat на основе текущей Telegram-сессии.
        Запускайте вручную только для рабочего аккаунта.
      </p>
      <div class="grid gap-2 sm:grid-cols-2 text-sm text-n-slate-11 mb-4">
        <p>Диалогов: 200</p>
        <p>Сообщений на диалог: 50</p>
        <p>Период: 180 дней</p>
        <p>Только личные чаты: да</p>
      </div>
      <div
        v-if="historyJob"
        class="grid gap-2 sm:grid-cols-2 rounded-lg bg-n-alpha-2 p-3 text-sm text-n-slate-11 mb-4"
      >
        <p>Status: {{ historyJob.status }}</p>
        <p>Диалогов просмотрено: {{ historyJob.total_dialogs }}</p>
        <p>Импортировано диалогов: {{ historyJob.imported_dialogs }}</p>
        <p>Импортировано сообщений: {{ historyJob.imported_messages }}</p>
        <p>Пропущено: {{ historyJob.skipped_dialogs }}</p>
        <p v-if="historyJob.error">Ошибка: {{ historyJob.error }}</p>
      </div>
      <div class="flex flex-wrap gap-2">
        <button
          class="button nice"
          type="button"
          :disabled="isImportLoading || historyImportRunning || !isConnected"
          @click="startHistoryImport"
        >
          Импортировать историю
        </button>
        <button
          class="button clear"
          type="button"
          :disabled="isImportLoading"
          @click="refreshHistoryImportStatus"
        >
          Проверить статус импорта
        </button>
        <button
          v-if="historyImportRunning"
          class="button alert"
          type="button"
          :disabled="isImportLoading"
          @click="stopHistoryImport"
        >
          Остановить импорт
        </button>
      </div>
      <p v-if="!isConnected" class="mt-3 text-sm text-n-amber-11">
        Сначала подключите Telegram account через QR.
      </p>
    </section>

  </div>
</template>
